import { useCallback, useEffect, useState } from 'react'
import { supabase, type CrmLead, type CrmOutreach, type CrmPipelineStatsRow } from '@/lib/supabase'

export type CrmLeadFilters = {
  pipeline_stage?: CrmLead['pipeline_stage'] | 'all'
  source?: CrmLead['source'] | 'all'
  search?: string
}

const PIPELINE_STAGES: CrmLead['pipeline_stage'][] = [
  'new',
  'contacted',
  'replied',
  'meeting',
  'proposal',
  'negotiation',
  'won',
  'lost',
]

export function useLeads(filters: CrmLeadFilters = {}) {
  const [leads, setLeads] = useState<CrmLead[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchLeads = useCallback(async () => {
    setLoading(true)
    setError(null)
    let query = supabase
      .from('crm_leads')
      .select('*')
      .order('created_at', { ascending: false })

    if (filters.pipeline_stage && filters.pipeline_stage !== 'all') {
      query = query.eq('pipeline_stage', filters.pipeline_stage)
    }
    if (filters.source && filters.source !== 'all') {
      query = query.eq('source', filters.source)
    }
    if (filters.search?.trim()) {
      const safe = filters.search.trim().replace(/,/g, '')
      const term = `%${safe}%`
      query = query.or(
        `email.ilike.${term},company.ilike.${term},full_name.ilike.${term},first_name.ilike.${term},last_name.ilike.${term}`
      )
    }

    const { data, error: err } = await query
    if (err) {
      setError(err.message)
      setLeads([])
    } else {
      setLeads((data ?? []) as CrmLead[])
    }
    setLoading(false)
  }, [filters.pipeline_stage, filters.source, filters.search])

  useEffect(() => {
    fetchLeads()
  }, [fetchLeads])

  return { leads, loading, error, refetch: fetchLeads }
}

export function useLead(id: string | null) {
  const [lead, setLead] = useState<CrmLead | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchLead = useCallback(async () => {
    if (!id) {
      setLead(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from('crm_leads')
      .select('*')
      .eq('id', id)
      .single()
    if (err) {
      setError(err.message)
      setLead(null)
    } else {
      setLead(data as CrmLead)
    }
    setLoading(false)
  }, [id])

  useEffect(() => {
    fetchLead()
  }, [fetchLead])

  return { lead, loading, error, refetch: fetchLead }
}

export function usePipelineStats() {
  const [stats, setStats] = useState<CrmPipelineStatsRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchStats = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data, error: err } = await supabase.from('crm_pipeline_stats').select('*')
    if (err) {
      setError(err.message)
      setStats([])
    } else {
      setStats((data ?? []) as CrmPipelineStatsRow[])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  return { stats, loading, error, refetch: fetchStats }
}

export function useOutreach(leadId: string | null) {
  const [outreach, setOutreach] = useState<CrmOutreach[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchOutreach = useCallback(async () => {
    if (!leadId) {
      setOutreach([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from('crm_outreach')
      .select('*')
      .eq('lead_id', leadId)
      .order('created_at', { ascending: false })
    if (err) {
      setError(err.message)
      setOutreach([])
    } else {
      setOutreach((data ?? []) as CrmOutreach[])
    }
    setLoading(false)
  }, [leadId])

  useEffect(() => {
    fetchOutreach()
  }, [fetchOutreach])

  return { outreach, loading, error, refetch: fetchOutreach }
}

export type UpdateLeadInput = Partial<
  Pick<
    CrmLead,
    | 'pipeline_stage'
    | 'notes'
    | 'assigned_to'
    | 'full_name'
    | 'email'
    | 'company'
    | 'title'
    | 'last_contacted_at'
  >
>

export async function updateLead(id: string, patch: UpdateLeadInput): Promise<{ error: string | null }> {
  const { error } = await supabase.from('crm_leads').update(patch).eq('id', id)
  return { error: error?.message ?? null }
}

export type CreateLeadInput = {
  full_name?: string | null
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  company?: string | null
  title?: string | null
  source?: CrmLead['source']
  pipeline_stage?: CrmLead['pipeline_stage']
  notes?: string | null
  assigned_to?: CrmLead['assigned_to']
}

export async function createLead(input: CreateLeadInput): Promise<{ data: CrmLead | null; error: string | null }> {
  const { data, error } = await supabase
    .from('crm_leads')
    .insert({
      full_name: input.full_name ?? null,
      first_name: input.first_name ?? null,
      last_name: input.last_name ?? null,
      email: input.email ?? null,
      company: input.company ?? null,
      title: input.title ?? null,
      source: input.source ?? 'manual',
      pipeline_stage: input.pipeline_stage ?? 'new',
      notes: input.notes ?? null,
      assigned_to: input.assigned_to ?? 'manual',
    })
    .select()
    .single()
  return { data: data as CrmLead | null, error: error?.message ?? null }
}

export type CreateOutreachInput = {
  lead_id: string
  type: CrmOutreach['type']
  direction: CrmOutreach['direction']
  subject?: string | null
  body?: string | null
  status?: CrmOutreach['status']
  sent_by: CrmOutreach['sent_by']
}

export async function createOutreach(
  input: CreateOutreachInput
): Promise<{ data: CrmOutreach | null; error: string | null }> {
  const { data, error } = await supabase
    .from('crm_outreach')
    .insert({
      lead_id: input.lead_id,
      type: input.type,
      direction: input.direction,
      subject: input.subject ?? null,
      body: input.body ?? null,
      status: input.status ?? 'sent',
      sent_by: input.sent_by,
    })
    .select()
    .single()
  return { data: data as CrmOutreach | null, error: error?.message ?? null }
}

export type CrmOutreachWithLead = CrmOutreach & {
  crm_leads?: { full_name: string | null; company: string | null; email: string | null } | null
}

export function useRecentOutreach(limit = 50) {
  const [outreach, setOutreach] = useState<CrmOutreachWithLead[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchOutreach = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from('crm_outreach')
      .select('*, crm_leads(full_name, company, email)')
      .order('created_at', { ascending: false })
      .limit(limit)
    if (err) {
      setError(err.message)
      setOutreach([])
    } else {
      setOutreach((data ?? []) as CrmOutreachWithLead[])
    }
    setLoading(false)
  }, [limit])

  useEffect(() => {
    fetchOutreach()
  }, [fetchOutreach])

  return { outreach, loading, error, refetch: fetchOutreach }
}

export { PIPELINE_STAGES }
